import { useId, type ComponentProps, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const inputClass =
  'h-9 w-full rounded-ctl border border-line bg-surface px-2.5 text-base placeholder:text-faint hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-55 aria-invalid:border-neg-ink';

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(inputClass, className)} {...props} />;
}

type FieldProps = {
  label: ReactNode;
  /** Inline validation message; wires aria-invalid + aria-describedby. */
  error?: string | null;
  hint?: ReactNode;
  className?: string;
  /** Render the control with the ids this field provides. */
  children: (ids: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
};

/** Label above a control, with an inline error under it (never a toast). */
export function Field({ label, error, hint, className, children }: FieldProps) {
  const id = useId();
  const messageId = `${id}-message`;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-xs font-medium text-muted">{label}</label>
      {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': error || hint ? messageId : undefined })}
      {error ? (
        <span id={messageId} className="text-xs text-neg-ink">{error}</span>
      ) : hint ? (
        <span id={messageId} className="text-xs text-muted">{hint}</span>
      ) : null}
    </div>
  );
}

/** A multi-line text box (reasons, notes), styled like Input. Pair with Field. */
export function Textarea({ className, rows = 3, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      rows={rows}
      className={cn(
        'w-full resize-y rounded-ctl border border-line bg-surface px-2.5 py-2 text-sm placeholder:text-faint hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-55 aria-invalid:border-neg-ink',
        className,
      )}
      {...props}
    />
  );
}
